/**
 * Spec grid and ownership-cost estimate for the acquisition cards: what an
 * airframe carries, how far it flies, how it is maintained and what it adds to
 * unit cost. Pure. The UI formats the numbers; nothing here touches state.
 */

import { getSeatsByClass, getTotalSeats } from '../economics/cabinMath';
import type { AircraftType, CabinConfiguration, ClassMap } from '../models';
import { ANNUAL_DEPRECIATION_RATE, DAYS_PER_YEAR } from './fleetMath';

/** Speed of sound at cruise altitude (ISA, 35,000 ft, about 295 m/s). Mach 1 in km/h. */
export const MACH_ONE_CRUISE_KMH = 1062;

/** Block hours per day assumed when spreading an aircraft's cost over its seats. */
export const ESTIMATED_BLOCK_HOURS_PER_DAY = 10;

/** Passenger and baggage weight per seat, used to net seats out of the payload limit. */
export const PASSENGER_AND_BAGGAGE_KG = 100;

export interface AircraftSpecInput {
  readonly type: AircraftType;
  readonly cabin: CabinConfiguration;
  /** Flight hours on the airframe; zero for a new aircraft. */
  readonly airframeHours: number;
  /** Fuel burn relative to new (1 for a new aircraft; a used listing carries its own multiplier). */
  readonly fuelBurnMultiplier: number;
}

export interface AircraftSpecGrid {
  readonly seatsByClass: ClassMap<number>;
  readonly totalSeats: number;
  /** Payload left after the configured passengers and their bags, kg. The catalogue has no belly volume. */
  readonly cargoPayloadKg: number;
  readonly rangeKm: number;
  readonly cruiseMach: number;
  readonly fuelBurnKgPerHour: number;
  readonly airframeHours: number;
  /** Flight hours until the next A-check. */
  readonly hoursToACheck: number;
  /** Flight hours until the next C-check. */
  readonly hoursToCCheck: number;
}

/** Hours until the next check of a repeating interval, given the hours already flown. */
export function hoursUntilNextCheck(airframeHours: number, intervalHours: number): number {
  if (intervalHours <= 0) return 0;
  const flown = Math.max(0, airframeHours);
  return intervalHours - (flown % intervalHours);
}

export function buildAircraftSpecGrid(input: AircraftSpecInput): AircraftSpecGrid {
  const { type, cabin } = input;
  const seatsByClass = getSeatsByClass(cabin);
  const totalSeats = getTotalSeats(cabin);
  return {
    seatsByClass,
    totalSeats,
    cargoPayloadKg: Math.max(0, type.maxPayloadKg - totalSeats * PASSENGER_AND_BAGGAGE_KG),
    rangeKm: type.maxRangeKm,
    cruiseMach: type.cruiseSpeedKmh / MACH_ONE_CRUISE_KMH,
    fuelBurnKgPerHour: type.baseFuelBurnKgHr * input.fuelBurnMultiplier,
    airframeHours: Math.max(0, input.airframeHours),
    hoursToACheck: hoursUntilNextCheck(input.airframeHours, type.maintenance.aCheckIntervalFlightHours),
    hoursToCCheck: hoursUntilNextCheck(input.airframeHours, type.maintenance.cCheckIntervalFlightHours),
  };
}

/**
 * Daily capital charge on a bought airframe: depreciation plus the cost of
 * money on the purchase price. This is an economic charge for comparison; no
 * cash leaves the company each day.
 */
export function purchasedDailyCapitalCostUSD(priceUSD: number, annualRatePct: number): number {
  return (priceUSD * (ANNUAL_DEPRECIATION_RATE + annualRatePct / 100)) / DAYS_PER_YEAR;
}

/**
 * Estimated ownership cost per available seat mile, in US cents, from the daily
 * capital charge spread over seats × cruise speed × ESTIMATED_BLOCK_HOURS_PER_DAY.
 * Fuel, crew and maintenance are left out, so this is the ownership share only.
 */
export function estimateOwnershipCasmCents(input: {
  readonly seats: number;
  readonly cruiseSpeedKmh: number;
  readonly dailyCapitalCostUSD: number;
}): number {
  const asmPerDay = input.seats * input.cruiseSpeedKmh * ESTIMATED_BLOCK_HOURS_PER_DAY;
  if (asmPerDay <= 0) return 0;
  return (input.dailyCapitalCostUSD / asmPerDay) * 100;
}
